import type { AssistantAction, AssistantActivity } from '@secretario/shared';

/**
 * Rascunhos que o Secretário deixa prontos (mensagem para um contato). O
 * servidor só prepara; quem envia é este aparelho, depois de o dono confirmar,
 * pela conversa protegida com a pessoa.
 */
const SENT_KEY = 'secretario.rascunhos.enviados';

function sentIds(): Set<string> {
  try {
    const stored = JSON.parse(window.localStorage.getItem(SENT_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch {
    return new Set();
  }
}

export function draftWasHandled(activityId: string): boolean {
  return sentIds().has(activityId);
}

export function markDraftHandled(activityId: string): void {
  const ids = sentIds();
  ids.add(activityId);
  try {
    window.localStorage.setItem(SENT_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    /* sem armazenamento: o cartão volta a aparecer na próxima abertura */
  }
}

/** A última ação de mensagem que ainda espera a confirmação do dono. */
export function pendingDraft(
  activities: AssistantActivity[],
): { activityId: string; action: Extract<AssistantAction, { kind: 'send_message' }> } | null {
  for (let index = activities.length - 1; index >= 0; index -= 1) {
    const activity = activities[index];
    if (activity?.action?.kind === 'send_message' && !draftWasHandled(activity.id)) {
      return { activityId: activity.id, action: activity.action };
    }
  }
  return null;
}

/** Envia o rascunho pela conversa com a pessoa. Devolve a conversa, para abrir depois. */
export async function sendDraftMessage(username: string, text: string): Promise<string> {
  const [{ ensureMatrixClient }, rooms] = await Promise.all([
    import('./matrix/client.js'),
    import('./matrix/rooms.js'),
  ]);
  const client = await ensureMatrixClient();
  if (!client) throw new Error('As conversas não estão disponíveis agora.');
  const roomId = await rooms.startDirectMessage(client, `@${username}:${client.getDomain()}`);
  await rooms.sendText(client, roomId, text);
  return roomId;
}
